import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import MaterialGoodsFields from "./MaterialGoodsFields";
import ServiceItemNotice from "./ServiceItemNotice";
import MaterialCertifications from "./MaterialCertifications";
import MaterialFileUpload from "./MaterialFileUpload";
import {
  newMaterialGoodsValidationSchema,
  newMaterialServiceValidationSchema,
  NewMaterialFormValues,
} from "./newMaterialFormValidationSchema";
import { capitalize, TO_BE_AGREED } from "../../utils";
import i18next from "../../i18n";
import { useFileInputUploader } from "../../hooks";
import { useSites } from "../../hooks/useSites";
import { useCreateMaterial } from "../../hooks/useMaterials";
import { useNotification, useUser } from "../../context";
import { CertificationRequest, MaterialType, TagResponse } from "../../types";
import TagPicker from "../carbon/TagPicker/TagPicker";
import Loader from "../UI/Loader/Loader";
import "./newMaterialForm.scss";

type MaterialFormKind = "GOOD" | "SERVICE";

const defaultValues: NewMaterialFormValues = {
  ean: "",
  globalMaterialName: "",
  measurementUnit: "",
  customMeasurementUnit: "",
  deliveryFormat: "",
  deliveryPlace: "",
  brand: "",
  details: "",
  comments: "",
};

/**
 * The create-material form (`POST /material`), rebuilt on the Carbon form primitives (Task 6) in
 * place of ~787 lines of MUI/Tailwind + hand-rolled `register` wiring — the same rebuild
 * `NewSiteForm.tsx`/`TagForm.tsx` went through, and this form's own template for the RHF+Yup+
 * `Form`/`TextField`/`SelectField` wiring, the "validate first, THEN open the confirm dialog"
 * fix, and the majority-required `(optional)` marking convention.
 *
 * Split into `MaterialGoodsFields`/`MaterialCertifications`/`MaterialFileUpload` purely to stay
 * under the 400-line-per-file budget — this component owns the type toggle, the RHF instance, the
 * confirm dialog, and payload assembly; each child owns its own slice of markup only.
 *
 * `certifications`/`tags`/`fileName`/the two confidentiality toggles stay plain `useState`, not
 * RHF fields — identical to the pre-Carbon form. None of them ever had a Yup rule
 * (`newMaterialFormValidationSchema.ts` has none either), so there is no validation-parity risk
 * in leaving them as local state; only `measurementUnit`/`deliveryPlace` — the two fields the
 * pre-Carbon bug actually lived on — are real, `Controller`-bound RHF fields here.
 */
const NewMaterialForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { openNotification } = useNotification();

  const [kind, setKind] = useState<MaterialFormKind | null>(null);
  const [isSpecConfidential, setIsSpecConfidential] = useState(false);
  const [materialConfidential, setMaterialConfidential] = useState(false);
  const [certifications, setCertifications] = useState<CertificationRequest[]>(
    []
  );
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [pendingValues, setPendingValues] =
    useState<NewMaterialFormValues | null>(null);
  // Which confirm-dialog button was pressed — read inside the create mutation's own `onSuccess`
  // (defined once, below) to decide between "reset and stay" (Create Another) and "navigate away"
  // (Create). A ref, not state: the mutation's callback closes over it at call time, not render
  // time, so it always reads the click that actually triggered the in-flight request.
  const createAnotherRef = useRef(false);

  const { data: sites } = useSites(user?.company?.id || "");

  const {
    data: uploadedSpecificationFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
  } = useFileInputUploader();

  const validationSchema =
    kind === "SERVICE"
      ? newMaterialServiceValidationSchema
      : newMaterialGoodsValidationSchema;

  const formOptions = useMemo(
    () => ({ resolver: yupResolver(validationSchema), defaultValues }),
    [validationSchema]
  );

  const { control, handleSubmit, reset, setError } =
    useForm<NewMaterialFormValues>(formOptions);

  const measurementUnit = useWatch({ control, name: "measurementUnit" });

  const resetFormState = () => {
    reset(defaultValues);
    setIsSpecConfidential(false);
    setMaterialConfidential(false);
    setCertifications([]);
    setTags([]);
    setFileName(null);
  };

  const { mutate: createMaterial, isPending: isCreatingMaterial } =
    useCreateMaterial(
      () => {
        openNotification(t("newMaterialForm.successMaterial"));
        if (createAnotherRef.current) {
          resetFormState();
        } else {
          navigate("/materials");
        }
      },
      (error: any) => {
        const errorType = error?.response?.data?.errorType;
        if (errorType === "DUPLICATED") {
          setError("ean", {
            type: "manual",
            message: t("newMaterialForm.repeated"),
          });
        } else {
          openNotification(t("newMaterialForm.error"));
        }
      }
    );

  const buildGoodsPayload = (values: NewMaterialFormValues): any => {
    const finalMeasurementUnit =
      values.measurementUnit === i18next.t("measurementUnits.other")
        ? values.customMeasurementUnit
        : values.measurementUnit;

    return {
      ean: values.ean,
      type: MaterialType.DIRECT_GOODS,
      deliveryPlace:
        values.deliveryPlace === TO_BE_AGREED
          ? null
          : { id: values.deliveryPlace },
      fileURL: uploadedSpecificationFileUrl || "",
      globalMaterialName: capitalize(values.globalMaterialName) || "",
      deliveryFormat: values.deliveryFormat
        ? capitalize(values.deliveryFormat)
        : "",
      measurementUnit: finalMeasurementUnit,
      details: values.details || "",
      comments: values.comments ? capitalize(values.comments, true) : "",
      brand: values.brand ? capitalize(values.brand, true) : "",
      confidentSpec: isSpecConfidential,
      confident: materialConfidential,
      certifications,
      tags: tags.map((tag) => ({ id: tag.id })),
      async: true,
    };
  };

  const buildServicePayload = (values: NewMaterialFormValues): any => ({
    ean: values.ean,
    globalMaterialName: capitalize(values.globalMaterialName) || "",
    type: MaterialType.SERVICES,
    deliveryPlace: null,
    fileURL: "",
    measurementUnit: "",
    comments: "",
    brand: "",
    confidentSpec: false,
    confident: false,
    certifications: [],
    tags: tags.map((tag) => ({ id: tag.id })),
    async: true,
  });

  // Only ever set from a submission that already passed Yup — `Form`'s own `handleSubmit` runs
  // validation first, same fix `NewSiteForm.tsx` documents for itself. The pre-Carbon form opened
  // its confirm modal unconditionally on click and only validated once a button INSIDE the modal
  // was pressed, so an empty form's field errors stayed hidden behind a confirmation dialog.
  const onValid = (values: NewMaterialFormValues) => {
    if (!kind) return;
    setPendingValues(values);
  };

  const handleConfirm = (shouldCreateAnother: boolean) => {
    if (!pendingValues) return;
    createAnotherRef.current = shouldCreateAnother;
    setPendingValues(null);
    const payload =
      kind === "SERVICE"
        ? buildServicePayload(pendingValues)
        : buildGoodsPayload(pendingValues);
    createMaterial(payload);
  };

  const selectKind = (nextKind: MaterialFormKind) => {
    setKind(nextKind);
    resetFormState();
  };

  // Same full-screen loader the pre-Carbon form showed while `POST /material` was in flight
  // (`if (isLoadingMaterials) return <Loader isFullScreen />;`) — the confirm dialog closes the
  // instant a button is pressed (see `handleConfirm`), so this is what tells the user something
  // is actually happening until the mutation's `onSuccess`/`onError` fires.
  if (isCreatingMaterial) return <Loader isFullScreen />;

  return (
    <div className="new-material-form cds-form-surface">
      <div className="new-material-form__type-picker">
        <p className="new-material-form__type-label cds--label">
          {t("newMaterialForm.type")}
        </p>
        <div className="new-material-form__type-buttons">
          <Button
            kind={kind === "GOOD" ? "primary" : "tertiary"}
            aria-pressed={kind === "GOOD"}
            onClick={() => selectKind("GOOD")}
          >
            {t("materialTypes.goods")}
          </Button>
          <Button
            kind={kind === "SERVICE" ? "primary" : "tertiary"}
            aria-pressed={kind === "SERVICE"}
            onClick={() => selectKind("SERVICE")}
          >
            {t("materialTypes.services")}
          </Button>
        </div>
      </div>

      {kind && (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onValid}
          noValidate
          className="new-material-form__form"
        >
          {kind === "SERVICE" && <ServiceItemNotice />}

          <div className="new-material-form__core-grid">
            <TextField
              name="ean"
              label={
                kind === "SERVICE"
                  ? t("newMaterialForm.eanService")
                  : t("newMaterialForm.ean")
              }
              control={control}
              required
            />
            <TextField
              name="globalMaterialName"
              label={
                kind === "SERVICE"
                  ? t("newMaterialForm.nameService")
                  : t("newMaterialForm.name")
              }
              control={control}
              required
            />
          </div>

          {kind === "GOOD" && (
            <>
              <MaterialGoodsFields
                control={control}
                measurementUnit={measurementUnit}
                sites={sites}
                materialConfidential={materialConfidential}
                onMaterialConfidentialChange={setMaterialConfidential}
              />

              <div className="new-material-form__tags">
                <TagPicker
                  selectedTags={tags}
                  onChange={setTags}
                  label={t("tags.tags")}
                  allowCreate
                  maxTags={10}
                />
                <p className="new-material-form__tags-hint">
                  {t("tags.tagsHint")}
                </p>
              </div>

              <MaterialCertifications
                certifications={certifications}
                onChange={setCertifications}
              />

              <hr />
              <MaterialFileUpload
                fileName={fileName}
                isUploading={isUploading}
                onFileSelected={(file) => {
                  uploadFile(file);
                  setFileName(file.name);
                }}
                onFileRemoved={() => setFileName(null)}
                isSpecConfidential={isSpecConfidential}
                onSpecConfidentialChange={setIsSpecConfidential}
              />
            </>
          )}

          <div className="new-material-form__actions">
            <Button as={Link} to="/materials" kind="secondary">
              {t("cta.cancel")}
            </Button>
            <Button type="submit" disabled={isUploading || isCreatingMaterial}>
              {t("cta.add")}
            </Button>
          </div>
        </Form>
      )}

      <ComposedModal
        open={pendingValues !== null}
        aria-label={t("newMaterialForm.save")}
        onClose={() => setPendingValues(null)}
      >
        <ModalHeader title={t("newMaterialForm.save")} />
        <ModalBody>
          <p>{t("newMaterialForm.saveQuestion")}</p>
        </ModalBody>
        <ModalFooter
          secondaryButtons={[
            {
              buttonText: t("cta.cancel"),
              onClick: () => setPendingValues(null),
            },
            {
              buttonText: t("newMaterialForm.createAnother"),
              onClick: () => handleConfirm(true),
            },
          ]}
          primaryButtonText={t("newMaterialForm.goToMaterials")}
          onRequestSubmit={() => handleConfirm(false)}
        >
          {null}
        </ModalFooter>
      </ComposedModal>
    </div>
  );
};

export default NewMaterialForm;
